import { EventEmitter } from 'events';
import { WhatsAppClient } from '../client/WhatsAppClient';
import { SessionState } from '../types';

export interface SessionConfig {
  tel: string;
  name?: string;
  autoRestart?: boolean;
  maxRestartAttempts?: number;
  restartDelay?: number;
  healthCheckInterval?: number;
  qrCodeTimeout?: number;
  connectionTimeout?: number;
  retryInterval?: number;
}

export interface SessionHealthMetrics {
  isConnected: boolean;
  lastHeartbeat: Date | null;
  connectionUptime: number;
  totalRestarts: number;
  lastError: string | null;
  responseTime: number;
  messagesSent: number;
  messagesReceived: number;
}

export interface SessionStore {
  save(tel: string, data: any): Promise<void>;
  load(tel: string): Promise<any>;
  delete(tel: string): Promise<void>;
  exists(tel: string): Promise<boolean>;
  list(): Promise<string[]>;
}

export class InMemorySessionStore implements SessionStore {
  private store: Map<string, any> = new Map();

  async save(tel: string, data: any): Promise<void> {
    this.store.set(tel, JSON.parse(JSON.stringify(data)));
  }

  async load(tel: string): Promise<any> {
    return this.store.get(tel) || null;
  }

  async delete(tel: string): Promise<void> {
    this.store.delete(tel);
  }

  async exists(tel: string): Promise<boolean> {
    return this.store.has(tel);
  }

  async list(): Promise<string[]> {
    return Array.from(this.store.keys());
  }
}

export class MongoSessionStore implements SessionStore {
  constructor(
    private config: {
      uri: string;
      database?: string;
      collection?: string;
    }
  ) {}

  async save(tel: string, data: any): Promise<void> {
    // This would integrate with actual MongoDB
    // For now, using in-memory fallback
    console.warn('MongoSessionStore not fully implemented, using in-memory storage');
  }

  async load(tel: string): Promise<any> {
    console.warn('MongoSessionStore not fully implemented, using in-memory storage');
    return null;
  }

  async delete(tel: string): Promise<void> {
    console.warn('MongoSessionStore not fully implemented, using in-memory storage');
  }

  async exists(tel: string): Promise<boolean> {
    console.warn('MongoSessionStore not fully implemented, using in-memory storage');
    return false;
  }

  async list(): Promise<string[]> {
    console.warn('MongoSessionStore not fully implemented, using in-memory storage');
    return [];
  }
}

export class SessionManager extends EventEmitter {
  private sessions: Map<string, SessionConfig> = new Map();
  private healthMetrics: Map<string, SessionHealthMetrics> = new Map();
  private healthCheckIntervals: Map<string, NodeJS.Timeout> = new Map();
  private restartAttempts: Map<string, number> = new Map();

  constructor(
    private client: WhatsAppClient,
    private sessionStore: SessionStore = new InMemorySessionStore(),
    private globalConfig: {
      autoRestart?: boolean;
      maxRestartAttempts?: number;
      restartDelay?: number;
      healthCheckInterval?: number;
    } = {}
  ) {
    super();
  }

  /**
   * Initialize a session with automatic restoration
   */
  async restoreSession(tel: string, config: Partial<SessionConfig> = {}): Promise<void> {
    const sessionConfig: SessionConfig = {
      tel,
      name: config.name || `Session-${tel}`,
      autoRestart: config.autoRestart ?? this.globalConfig.autoRestart ?? true,
      maxRestartAttempts: config.maxRestartAttempts ?? this.globalConfig.maxRestartAttempts ?? 3,
      restartDelay: config.restartDelay ?? this.globalConfig.restartDelay ?? 30000,
      healthCheckInterval: config.healthCheckInterval ?? this.globalConfig.healthCheckInterval ?? 60000,
      qrCodeTimeout: config.qrCodeTimeout ?? 120000,
      connectionTimeout: config.connectionTimeout ?? 60000,
      retryInterval: config.retryInterval ?? 5000,
      ...config
    };

    this.sessions.set(tel, sessionConfig);
    this.initializeHealthMetrics(tel);

    try {
      // Try to restore from storage first
      const existingSession = await this.sessionStore.load(tel);
      if (existingSession) {
        this.emit('session:restoring', { tel, data: existingSession });
      }

      // Initialize session
      await this.initializeSession(tel);
      
      // Start health monitoring
      this.startHealthMonitoring(tel);

      this.emit('session:restored', { tel, config: sessionConfig });

    } catch (error) {
      this.emit('session:restore:failed', { tel, error: error instanceof Error ? error.message : String(error) });
      
      if (sessionConfig.autoRestart) {
        await this.scheduleRestart(tel);
      }
      
      throw error;
    }
  }

  /**
   * Initialize a session
   */
  private async initializeSession(tel: string): Promise<void> {
    try {
      this.emit('session:initializing', { tel });

      // Create session if it doesn't exist
      const existingSessions = await this.client.sessions.list();
      const sessionExists = existingSessions.sessions?.some((s: any) => s.tel === tel) || false;

      if (!sessionExists) {
        const config = this.sessions.get(tel);
        if (config) {
          await this.client.sessions.create({
            tel,
            name: config.name || tel,
            autoRestart: config.autoRestart
          });
        }
      }

      // Initialize connection
      await this.client.sessions.initialize(tel);

      // Wait for QR code or connection
      await this.waitForConnection(tel);

      this.updateHealthMetrics(tel, { isConnected: true, lastError: null });
      this.emit('session:initialized', { tel });

    } catch (error) {
      this.updateHealthMetrics(tel, { isConnected: false, lastError: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }

  /**
   * Wait for session connection
   */
  private async waitForConnection(tel: string): Promise<void> {
    const config = this.sessions.get(tel)!;
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const checkConnection = async () => {
        try {
          const session = await this.client.sessions.get(tel);
          
          if (session.state === SessionState.CONNECTED) {
            resolve();
            return;
          }

          if (session.qr) {
            this.emit('session:qr', { tel, qr: session.qr });
          }

          // Check timeout
          if (Date.now() - startTime > config.connectionTimeout!) {
            reject(new Error('Connection timeout'));
            return;
          }

          // Continue checking
          setTimeout(checkConnection, config.retryInterval);

        } catch (error) {
          reject(error);
        }
      };

      checkConnection();
    });
  }

  /**
   * Start health monitoring for a session
   */
  private startHealthMonitoring(tel: string): void {
    const config = this.sessions.get(tel);
    if (!config) return;

    // Clear existing interval
    this.stopHealthMonitoring(tel);

    const interval = setInterval(async () => {
      await this.performHealthCheck(tel);
    }, config.healthCheckInterval);

    this.healthCheckIntervals.set(tel, interval);
    this.emit('health:monitoring:started', { tel });
  }

  /**
   * Stop health monitoring for a session
   */
  private stopHealthMonitoring(tel: string): void {
    const interval = this.healthCheckIntervals.get(tel);
    if (interval) {
      clearInterval(interval);
      this.healthCheckIntervals.delete(tel);
      this.emit('health:monitoring:stopped', { tel });
    }
  }

  /**
   * Perform health check for a session
   */
  private async performHealthCheck(tel: string): Promise<void> {
    const startTime = Date.now();
    
    try {
      // Check session status
      const session = await this.client.sessions.get(tel);
      const responseTime = Date.now() - startTime;

      const isHealthy = session.state === SessionState.CONNECTED;
      
      this.updateHealthMetrics(tel, {
        isConnected: isHealthy,
        lastHeartbeat: new Date(),
        responseTime,
        lastError: isHealthy ? null : `Session state: ${session.state}`
      });

      if (isHealthy) {
        this.emit('health:check:success', { tel, responseTime, state: session.state });
      } else {
        this.emit('health:check:unhealthy', { tel, state: session.state });
        
        const config = this.sessions.get(tel);
        if (config?.autoRestart) {
          await this.handleUnhealthySession(tel);
        }
      }

    } catch (error) {
      this.updateHealthMetrics(tel, {
        isConnected: false,
        lastError: error instanceof Error ? error.message : String(error),
        responseTime: Date.now() - startTime
      });

      this.emit('health:check:failed', { tel, error: error instanceof Error ? error.message : String(error) });
      
      const config = this.sessions.get(tel);
      if (config?.autoRestart) {
        await this.handleUnhealthySession(tel);
      }
    }
  }

  /**
   * Handle unhealthy session
   */
  private async handleUnhealthySession(tel: string): Promise<void> {
    const attempts = this.restartAttempts.get(tel) || 0;
    const config = this.sessions.get(tel)!;

    if (attempts >= config.maxRestartAttempts!) {
      this.emit('session:restart:max_attempts', { tel, attempts });
      return;
    }

    await this.scheduleRestart(tel);
  }

  /**
   * Schedule session restart
   */
  private async scheduleRestart(tel: string): Promise<void> {
    const config = this.sessions.get(tel)!;
    const currentAttempts = this.restartAttempts.get(tel) || 0;
    
    this.restartAttempts.set(tel, currentAttempts + 1);
    
    this.emit('session:restart:scheduled', { 
      tel, 
      attempt: currentAttempts + 1, 
      delay: config.restartDelay 
    });

    setTimeout(async () => {
      await this.restartSession(tel);
    }, config.restartDelay);
  }

  /**
   * Restart a session
   */
  async restartSession(tel: string): Promise<void> {
    this.emit('session:restarting', { tel });

    try {
      // Stop health monitoring during restart
      this.stopHealthMonitoring(tel);

      // Reset session
      await this.client.sessions.reset(tel);
      
      // Wait a moment for cleanup
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Reinitialize
      await this.initializeSession(tel);
      
      // Restart health monitoring
      this.startHealthMonitoring(tel);

      // Reset restart attempts on success
      this.restartAttempts.delete(tel);
      
      this.updateHealthMetrics(tel, metrics => ({
        totalRestarts: metrics.totalRestarts + 1
      }));

      this.emit('session:restarted', { tel });

    } catch (error) {
      this.emit('session:restart:failed', { tel, error: error instanceof Error ? error.message : String(error) });
      
      const config = this.sessions.get(tel)!;
      if (config.autoRestart) {
        await this.scheduleRestart(tel);
      }
    }
  }

  /**
   * Initialize health metrics for a session
   */
  private initializeHealthMetrics(tel: string): void {
    this.healthMetrics.set(tel, {
      isConnected: false,
      lastHeartbeat: null,
      connectionUptime: 0,
      totalRestarts: 0,
      lastError: null,
      responseTime: 0,
      messagesSent: 0,
      messagesReceived: 0
    });
  }

  /**
   * Update health metrics
   */
  private updateHealthMetrics(tel: string, updates: Partial<SessionHealthMetrics> | ((current: SessionHealthMetrics) => Partial<SessionHealthMetrics>)): void {
    const current = this.healthMetrics.get(tel);
    if (!current) return;

    const newMetrics = typeof updates === 'function' 
      ? { ...current, ...updates(current) }
      : { ...current, ...updates };

    this.healthMetrics.set(tel, newMetrics);
    this.emit('health:metrics:updated', { tel, metrics: newMetrics });
  }

  /**
   * Get health metrics for a session
   */
  getHealthMetrics(tel: string): SessionHealthMetrics | null {
    return this.healthMetrics.get(tel) || null;
  }

  /**
   * Get health metrics for all sessions
   */
  getAllHealthMetrics(): Map<string, SessionHealthMetrics> {
    return new Map(this.healthMetrics);
  }

  /**
   * Remove a session from management
   */
  async removeSession(tel: string): Promise<void> {
    // Stop health monitoring
    this.stopHealthMonitoring(tel);

    // Clean up data structures
    this.sessions.delete(tel);
    this.healthMetrics.delete(tel);
    this.restartAttempts.delete(tel);

    // Remove from storage
    await this.sessionStore.delete(tel);

    this.emit('session:removed', { tel });
  }

  /**
   * Get all managed sessions
   */
  getSessions(): string[] {
    return Array.from(this.sessions.keys());
  }

  /**
   * Get session configuration
   */
  getSessionConfig(tel: string): SessionConfig | null {
    return this.sessions.get(tel) || null;
  }

  /**
   * Update session configuration
   */
  updateSessionConfig(tel: string, updates: Partial<SessionConfig>): void {
    const current = this.sessions.get(tel);
    if (!current) {
      throw new Error(`Session ${tel} not found`);
    }

    const updated = { ...current, ...updates };
    this.sessions.set(tel, updated);

    // Restart health monitoring if interval changed
    if (updates.healthCheckInterval) {
      this.startHealthMonitoring(tel);
    }

    this.emit('session:config:updated', { tel, config: updated });
  }

  /**
   * Graceful shutdown of all sessions
   */
  async shutdown(): Promise<void> {
    this.emit('manager:shutting_down');

    const sessions = this.getSessions();
    
    // Stop all health monitoring
    for (const tel of sessions) {
      this.stopHealthMonitoring(tel);
    }

    // Save session states
    for (const tel of sessions) {
      try {
        const session = await this.client.sessions.get(tel);
        await this.sessionStore.save(tel, { state: session.state, lastSeen: new Date() });
      } catch (error) {
        this.emit('manager:save:error', { tel, error: error instanceof Error ? error.message : String(error) });
      }
    }

    this.emit('manager:shutdown:complete');
  }
}